import React, { useCallback, useState } from "react";
import { FaRegCopy } from "react-icons/fa";
import { Form, notification } from "antd";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import printJS from "print-js";
import api from "../../../../service";
import generateHeaders from "../../../../utils/generateHeaders";
import { base64ToArrayBuffer, mergePdfs } from "../../../../utils/file";
import { DotIcon } from "../../ServicePanel/card-icons";
import ConfirmationModalContent from "../ConfirmationModalContent";
import {
  ButtonFormConfirm,
  CredentialsListContainer,
  CredentialsListLabel,
  FooterContainer,
  HeaderModal,
  ModalSubTitle,
  ModalTitle,
  ModalWithdrawalAndDisassociate,
  StyledInput,
} from "./styles";
import { StyledFormItem } from "../../../../components/ModalNewCollaborator/styles";

const DuplicateModal = ({
  defaulter,
  fetchData,
  open,
  setOpen,
  selectedRowKeys,
  setSelectedRows,
  availableCredentials,
  updateAvailableCredentials,
}) => {
  const hasAvailableCredentials = availableCredentials > 0;
  const [isDefaulter, setIsDefaulter] = useState(defaulter && hasAvailableCredentials);
  const intl = useIntl();
  const [duplicateForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { token } = useSelector((state) => state.auth);
  const { TextArea } = StyledInput;
  const history = useHistory();

  const reset = useCallback(() => {
    setLoading(false);
    fetchData();
    setSelectedRows([]);
    setIsDefaulter(defaulter && hasAvailableCredentials);
  }, [
    defaulter,
    setSelectedRows,
    fetchData,
    hasAvailableCredentials,
  ]);

  const onFinishDuplicate = useCallback(async () => {
    try {
      const options = generateHeaders(token);
      setLoading(true);

      const [{
        id: credentialId,
        credentialType: selectedCredentialType
      } = {}] = selectedRowKeys;
      const values = await duplicateForm.validateFields();

      const sanitizedValues = JSON.stringify({
        credentialId,
        overrideReason: values?.reason || "",
        channel: "CAEX",
      });

      const response = await api.post(
        "/credential-reports/duplicate-and-print",
        sanitizedValues,
        options
      );

      setOpen(false);
      duplicateForm.resetFields();

      const credentialReports = response.data?.reportOutputList || [];

      if (credentialReports.length > 0) {
        const arrayBuffers = credentialReports.map((apiResult) => {
          return base64ToArrayBuffer(apiResult.file);
        });

        const base64String = btoa(
          [].reduce.call(
            new Uint8Array(await mergePdfs(arrayBuffers)),
            function (p, c) {
              return p + String.fromCharCode(c);
            },
            ""
          )
        );

        printJS({
          printable: base64String,
          type: "pdf",
          base64: true,
        });
      }

      reset();
      updateAvailableCredentials(selectedCredentialType);
    } catch (error) {
      notification.error({
        message: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({
          id: "api.error." + (error.response?.data?.code || "unknown"),
        }),
      });
      setLoading(false);
    }
  }, [duplicateForm, reset, selectedRowKeys, setOpen, token]);

  const handleCloseModal = () => {
    reset();
    setOpen(false);
  };

  const redirectToSalesPage = () => {
    reset();
    setOpen(false);
    history.push("/staff-access/services/sales");
  };

  const handleDefaulterConfirmation = () => {
    setIsDefaulter(false);
  };

  return (
    <ModalWithdrawalAndDisassociate
      width={658}
      height={"auto"}
      closable={false}
      onCancel={handleCloseModal}
      visible={open}
      footer={""}
      destroyOnClose
      centered={true}
    >
      {!isDefaulter && (
        <div>
          <HeaderModal>
            <div className="icon">
              <FaRegCopy
                style={{ fontSize: "18px", color: "rgba(6, 142, 195, 1)" }}
              />
            </div>
            <ModalTitle>
              {intl.formatMessage({ id: "second_copy" })}
            </ModalTitle>
          </HeaderModal>
          <ModalSubTitle>
            {intl.formatMessage({
              id: hasAvailableCredentials
                ? "confirm_duplicate_of_credential"
                : "refuse_duplicate_of_credential",
            })}
          </ModalSubTitle>
          <br />
          <br />
          <ModalSubTitle>
            {intl.formatMessage(
              { id: "available_credentials_with_colon" },
            )}
          </ModalSubTitle>
          <ModalSubTitle style={{ fontWeight: "bold" }}>
            {availableCredentials}
          </ModalSubTitle>
          <CredentialsListContainer>
            {selectedRowKeys.map((row) => (
              <CredentialsListLabel key={row.id}>
                <DotIcon color="#D9D9D9" size={7} />
                {row.collaboratorName}
              </CredentialsListLabel>
            ))}
          </CredentialsListContainer>
          <br />
          <Form
            form={duplicateForm}
            onFinish={onFinishDuplicate}
            layout="vertical"
          >
            {defaulter && hasAvailableCredentials && (
              <StyledFormItem
                label={intl.formatMessage({ id: "provide_reason" })}
                name={"reason"}
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: "error.field_required",
                    }),
                  },
                ]}
              >
                <TextArea />
              </StyledFormItem>
            )}
          </Form>

          <FooterContainer>
            <div></div>
            <div style={{ marginRight: "10px" }}>
              <ButtonFormConfirm
                onClick={handleCloseModal}
                style={{ border: "none" }}
              >
                {intl.formatMessage({ id: "cancel" })}
              </ButtonFormConfirm>
              { hasAvailableCredentials ? (
                <>
                  <ButtonFormConfirm
                    loading={loading}
                    onClick={() => duplicateForm.submit()}
                    style={{
                      backgroundColor: "rgba(6, 142, 195, 1)",
                      color: "rgba(255, 255, 255, 1)",
                    }}
                  >
                    {intl.formatMessage({ id: "confirm" })}
                  </ButtonFormConfirm>
                </>
              ) : (
                <>
                  <ButtonFormConfirm
                    onClick={redirectToSalesPage}
                    style={{
                      backgroundColor: "rgba(6, 142, 195, 1)",
                      color: "rgba(255, 255, 255, 1)",
                    }}
                  >
                    {intl.formatMessage({ id: "purchase" })}
                  </ButtonFormConfirm>
                </>
              )}
            </div>
          </FooterContainer>
        </div>
      )}

      {isDefaulter && (
        <ConfirmationModalContent
          description={intl.formatMessage({ id: "credential_locked" })}
          confirmationText={"open_exception"}
          cancelText={"close"}
          confirmationFunction={handleDefaulterConfirmation}
          cancelFunction={handleCloseModal}
        />
      )}
    </ModalWithdrawalAndDisassociate>
  );
};

export default DuplicateModal;
